// Shapes returned by jsonb RPCs (public catalog, availability, tracking).
// Table row types come from the generated `database.ts` (npm run db:types).
import type { Database } from './database'

export type PriceType = Database['public']['Enums']['price_type']
export type AppointmentStatus = Database['public']['Enums']['appointment_status']
export type BusinessRole = Database['public']['Enums']['business_role']
export type BusinessStatus = Database['public']['Enums']['business_status']
export type ThemeName = 'beauty_editorial' | 'modern_minimal' | 'luxury_dark'
export type FontPair = 'editorial' | 'modern' | 'classic'

export type Tables<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row']
export type TablesInsert<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Insert']
export type TablesUpdate<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Update']

export interface PublicBusiness {
  id: string
  slug: string
  name: string
  tagline: string | null
  description: string | null
  business_type: string
  phone: string | null
  whatsapp: string | null
  email: string | null
  address_line: string | null
  city: string | null
  country_code: string
  latitude: number | null
  longitude: number | null
  maps_url: string | null
  social_links: Record<string, string>
  currency: string
  timezone: string
  theme: ThemeName
  brand_primary: string | null
  brand_secondary: string | null
  font_pair: FontPair
  logo_path: string | null
  cover_path: string | null
  status: BusinessStatus
}

export interface PublicSettings {
  slot_interval_minutes: number
  min_lead_minutes: number
  max_advance_days: number
  approval_mode: 'manual' | 'auto'
  allow_staff_selection: boolean
  show_staff_publicly: boolean
  show_prices_publicly: boolean
  max_items_per_booking: number
  booking_policy: string | null
  cancellation_policy: string | null
  customer_cancel_cutoff_minutes: number
}

export interface PublicVariant {
  id: string
  name: string
  price_cents: number | null
  duration_minutes: number
}

export interface PublicAddon {
  id: string
  name: string
  description: string | null
  price_cents: number | null
  duration_minutes: number
}

export interface PublicService {
  id: string
  category_id: string | null
  name: string
  short_description: string | null
  description: string | null
  image_path: string | null
  price_type: PriceType
  price_cents: number | null
  duration_minutes: number
  featured: boolean
  allow_addons: boolean
  sort_order: number
  variants: PublicVariant[]
  addons: PublicAddon[]
  staff_ids: string[]
}

export interface PublicCategory {
  id: string
  name: string
  description: string | null
  image_path: string | null
  icon: string | null
  color: string | null
  sort_order: number
}

export interface PublicStaff {
  id: string
  display_name: string
  title: string | null
  bio: string | null
  photo_path: string | null
}

export interface PublicCatalog {
  business: PublicBusiness
  settings: PublicSettings
  hours: { weekday: number; opens_at: string; closes_at: string }[]
  categories: PublicCategory[]
  services: PublicService[]
  staff: PublicStaff[]
  gallery: { id: string; storage_path: string; caption: string | null }[]
}

/** What the booking flow sends to the engine. Prices are NEVER sent. */
export interface BookingItemInput {
  service_id: string
  variant_id?: string | null
  addon_ids?: string[]
}

export interface Slot {
  starts_at: string
  ends_at?: string
  staff_ids: string[]
}

export interface SlotsResponse {
  date: string
  timezone: string
  duration_minutes: number
  slots: Slot[]
  closed: boolean
}

export interface BookableDatesResponse {
  timezone: string
  today: string
  dates: string[]
}

export interface CreateBookingResponse {
  appointment_id: string
  reference_code: string
  status: AppointmentStatus
  starts_at: string
  ends_at: string
  hold_expires_at: string
  total_price_cents: number
  price_is_estimate: boolean
  customer_email: string
  access_token: string
  /** Only present in local development without an email provider. */
  dev_verification_code?: string
  email_delivery: 'sent' | 'not_configured' | 'failed'
}

export interface VerifyBookingResponse {
  ok: boolean
  status: AppointmentStatus
  error?: string
  attempts_left?: number
}

export interface TrackedBooking {
  reference_code: string
  status: AppointmentStatus
  starts_at: string
  ends_at: string
  total_price_cents: number
  price_is_estimate: boolean
  currency: string
  total_duration_minutes: number
  hold_expires_at: string | null
  status_reason: string | null
  customer_first_name: string
  customer_email_masked: string | null
  can_cancel: boolean
  cancellation_policy: string | null
  business: {
    name: string
    slug: string
    phone: string | null
    whatsapp: string | null
    address_line: string | null
    city: string | null
    maps_url: string | null
    timezone: string
    logo_path: string | null
    theme: ThemeName
    brand_primary: string | null
    brand_secondary: string | null
    font_pair: FontPair
  }
  items: {
    service_name: string
    variant_name: string | null
    price_type: PriceType
    line_total_cents: number
    duration_minutes: number
    addons: string[]
    starts_at: string
    ends_at: string
    staff_name: string | null
  }[]
  proposal: {
    starts_at: string
    ends_at: string
    message: string | null
    expires_at: string
    staff_names: string[] | null
  } | null
}

export interface DashboardStats {
  today: number
  pending: number
  by_status: Partial<Record<AppointmentStatus, number>>
  estimated_revenue_cents: number
  estimated_includes_estimates: boolean
  recorded_revenue_cents: number
  new_customers: number
  returning_customers: number
  series: { date: string; count: number }[]
  top_services: { name: string; count: number }[]
  top_staff: { name: string; count: number }[]
}

export interface CustomerRow {
  id: string
  full_name: string
  email: string | null
  phone: string | null
  created_at: string
  marketing_opt_in: boolean
  visits: number
  cancellations: number
  no_shows: number
  last_visit: string | null
  next_appointment: string | null
}

export const STATUS_LABEL: Record<AppointmentStatus, string> = {
  awaiting_verification: 'Por verificar',
  pending_approval: 'Pendiente',
  confirmed: 'Confirmada',
  change_proposed: 'Cambio propuesto',
  in_progress: 'En curso',
  completed: 'Completada',
  rejected: 'Rechazada',
  cancelled: 'Cancelada',
  expired: 'Vencida',
  no_show: 'No asistió',
}

export const ROLE_LABEL: Record<BusinessRole, string> = {
  owner: 'Propietaria/o',
  admin: 'Administración',
  receptionist: 'Recepción',
  staff: 'Profesional',
}
